/* global fetch, window, setTimeout */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import AppPanel from '../../fw/AppPanel.react';
import Table from 'react-bootstrap/lib/Table';
import './OverdueBillsList.scss';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import EnqueueOverdueBillsFetchJob from '../EnqueueOverdueBillsFetchJob.react';
import EnqueueOverdueBillsNotifyJob from './EnqueueOverdueBillsNotifyJob.react';
import DeleteOrganisationOverdueBill from '../DeleteOrganisationOverdueBill.react';
import UpdateOrganisationOverdueBillIgnore from './UpdateOrganisationOverdueBillIgnore.react.js';
import Relay from 'react-relay';
import showNotification, {showJobNotification} from '../../app/notification.js';
// quick action removed; using rules-based notify job instead
import DownloadOrganisationOverdueBillDocument from './DownloadOrganisationOverdueBillDocument.react.js';
import GetOrganizationsWithUnpaidInvoices from './GetOrganizationsWithUnpaidInvoices.react.js';
import GetGlobalUnpaidInvoiceSummary from './GetGlobalUnpaidInvoiceSummary.react.js';

import * as BrowserPermission from '../../BrowserPermission';
import * as Permission from '../../../common/util/Permission';

// Global search mutation
class SearchInvoicesGlobalMutation extends Relay.Mutation {
    getMutation() {
        return Relay.QL`mutation {searchInvoicesGlobal}`;
    }

    getVariables() {
        return {
            query: this.props.query,
            limit: this.props.limit || 100
        };
    }

    getFatQuery() {
        return Relay.QL`
            fragment on SearchInvoicesGlobalPayload {
                invoices {
                    bill {
                        id
                        code
                        variableSymbol
                        dueDate
                        amountToPay
                        currencyCode
                        totalAmount
                    }
                    organisation {
                        id
                        name
                    }
                }
                totalCount
            }
        `;
    }

    getConfigs() {
        return [{
            type: 'REQUIRED_CHILDREN',
            children: [Relay.QL`
                fragment on SearchInvoicesGlobalPayload {
                    invoices {
                        bill {
                            id
                            code
                            variableSymbol
                            dueDate
                            amountToPay
                            currencyCode
                            totalAmount
                        }
                        organisation {
                            id
                            name
                        }
                    }
                    totalCount
                }
            `]
        }];
    }
}

const messages = defineMessages({
    title: {
        id: 'invoices.title',
        defaultMessage: 'Invoices'
    },
    titleUnpaidOverdue: {
        id: 'invoices.title.unpaidOverdue',
        defaultMessage: 'Unpaid overdue invoices'
    },
    loadFromFlexiOrg: {
        id: 'invoices.loadFromFlexiOrg',
        defaultMessage: 'Load invoices (this org.)'
    },
    loadFromFlexiAll: {
        id: 'invoices.loadFromFlexiAll',
        defaultMessage: 'Load invoices (all org.)'
    },
    columnCode: {
        id: 'invoices.column.code',
        defaultMessage: 'Number'
    },
    columnVariableSymbol: {
        id: 'invoices.column.varSym',
        defaultMessage: 'Variable symbol'
    },
    columnDueDate: {
        id: 'invoices.column.dueDate',
        defaultMessage: 'Due date'
    },
    columnTotalAmount: {
        id: 'invoices.column.totalAmount',
        defaultMessage: 'Total amount'
    },
    columnAmountToPay: {
        id: 'invoices.column.amountToPay',
        defaultMessage: 'Amount to pay'
    },
    columnCurrency: {
        id: 'invoices.column.currency',
        defaultMessage: 'Currency'
    },
    columnDaysOverdue: {
        id: 'invoices.column.daysOverdue',
        defaultMessage: 'Days overdue'
    },
    columnDocuments: {
        id: 'invoices.column.documents',
        defaultMessage: 'Documents'
    },
    columnActions: {
        id: 'invoices.column.actions',
        defaultMessage: 'Actions'
    },
	columnSelect: {
		id: 'invoices.column.select',
		defaultMessage: 'Select'
	},
    title: {
        id: 'metadata.invoices.list',
        defaultMessage: 'Invoices'
    },
    filterOnlyUnpaidOverdue: {
        id: 'invoices.filter.onlyUnpaidOverdue',
        defaultMessage: 'Only unpaid'
    },
    searchPlaceholder: {
        id: 'invoices.search.placeholder',
        defaultMessage: 'Search by number or variable symbol...'
    },
    searchOrganizationsPlaceholder: {
        id: 'invoices.search.organizations.placeholder',
        defaultMessage: 'Search organizations by name...'
    },
    searchResultsCount: {
        id: 'invoices.search.resultsCount',
        defaultMessage: 'Showing {count} invoices (filtered by: {query})'
    },
    searchNoResults: {
        id: 'invoices.search.noResults',
        defaultMessage: 'No invoices found matching "{query}"'
    },
    searchGlobal: {
        id: 'invoices.search.global',
        defaultMessage: 'Search in all organizations'
    },
    searchGlobalResults: {
        id: 'invoices.search.globalResults',
        defaultMessage: 'Found in organizations: {orgs}'
    },
    totalUnpaid: {
        id: 'invoices.totalUnpaid',
        defaultMessage: 'Total unpaid:'
    },
    columnOrganisation: {
        id: 'invoices.column.organisation',
        defaultMessage: 'Organization'
    },
    pageSizeLabel: {
        id: 'invoices.pageSize',
        defaultMessage: 'Page size'
    },
    confirmNotifyOrg: {
        id: 'invoices.confirm.notifyOrg',
        defaultMessage: 'Are you sure you want to run rules for this organisation?'
    },
    confirmNotifyAll: {
        id: 'invoices.confirm.notifyAll',
        defaultMessage: 'Are you sure you want to run rules for all organisations?'
	},
	deleteSelected: {
		id: 'invoices.deleteSelected',
		defaultMessage: 'Delete selected'
	},
	confirmDeleteSelected: {
		id: 'invoices.confirm.deleteSelected',
		defaultMessage: 'Delete selected invoices?'
	},
	ignoreSelected: {
		id: 'invoices.ignoreSelected',
		defaultMessage: 'Ignore selected'
	},
	confirmIgnoreSelected: {
		id: 'invoices.confirm.ignoreSelected',
		defaultMessage: 'Ignore selected invoices in rules?'
	},
	unignoreSelected: {
		id: 'invoices.unignoreSelected',
		defaultMessage: 'Unignore selected'
	},
	confirmUnignoreSelected: {
		id: 'invoices.confirm.unignoreSelected',
		defaultMessage: 'Remove ignore flag for selected invoices?'
	},
	advancePayment: {
		id: 'invoices.advancePayment',
		defaultMessage: 'Advance'
	},
	advancePaymentTooltip: {
		id: 'invoices.advancePaymentTooltip',
		defaultMessage: 'This is an advance payment or proforma - notifications are disabled'
	},
	ignoredByLabel: {
		id: 'invoices.ignoredByLabel',
		defaultMessage: 'Ignored (label)'
	},
	ignoredByLabelTooltip: {
		id: 'invoices.ignoredByLabelTooltip',
		defaultMessage: 'Automatically ignored by FlexiBee label. Remove label in FlexiBee to unignore.'
	},
	ignoredManually: {
		id: 'invoices.ignoredManually',
		defaultMessage: 'Ignored (manual)'
	},
	ignoredManuallyTooltip: {
		id: 'invoices.ignoredManuallyTooltip',
		defaultMessage: 'Manually marked as ignored. Uncheck to process again.'
	},
	cannotChangeLabel: {
		id: 'invoices.cannotChangeLabel',
		defaultMessage: 'This invoice is ignored by FlexiBee label. Remove the label in FlexiBee to unignore.'
	},
	legendTitle: {
		id: 'invoices.legend.title',
		defaultMessage: 'Legend'
	},
	legendPaid: {
		id: 'invoices.legend.paid',
		defaultMessage: 'Paid'
	},
	legendPartial: {
		id: 'invoices.legend.partial',
		defaultMessage: 'Partially paid'
	},
	legendOverdue: {
		id: 'invoices.legend.overdue',
		defaultMessage: 'Overdue and unpaid'
	},
	legendSelected: {
		id: 'invoices.legend.selected',
		defaultMessage: 'Selected invoice (detail displayed)'
	},
	developerActions: {
		id: 'invoices.developerActions',
		defaultMessage: 'Actions'
	},
	// Organizations view messages
	filterAll: {
		id: 'invoices.filter.all',
		defaultMessage: 'All invoices'
	},
	filterUnpaid: {
		id: 'invoices.filter.unpaid',
		defaultMessage: 'Only unpaid'
	},
	filterOrganizations: {
		id: 'invoices.filter.organizations',
		defaultMessage: 'Organizations'
	},
	loadingOrganizations: {
		id: 'invoices.organizations.loading',
		defaultMessage: 'Loading organizations...'
	},
	noOrganizationsFound: {
		id: 'invoices.organizations.noFound',
		defaultMessage: 'No organizations with unpaid invoices found'
	},
	columnOrganization: {
		id: 'invoices.organizations.column.name',
		defaultMessage: 'Organization'
	},
	columnInvoiceCount: {
		id: 'invoices.organizations.column.count',
		defaultMessage: 'Invoices'
	},
	columnTotalCZK: {
		id: 'invoices.organizations.column.totalCZK',
		defaultMessage: 'Total CZK'
	},
	columnTotalEUR: {
		id: 'invoices.organizations.column.totalEUR',
		defaultMessage: 'Total EUR'
	},
	columnTotalOther: {
		id: 'invoices.organizations.column.totalOther',
		defaultMessage: 'Other'
	},
	columnOldestDueDate: {
		id: 'invoices.organizations.column.oldestDue',
		defaultMessage: 'Oldest Due Date'
	},
	filterBlockedAll: {
		id: 'invoices.organizations.filter.allBlocked',
		defaultMessage: 'All organizations'
	},
	filterBlockedOnly: {
		id: 'invoices.organizations.filter.blockedOnly',
		defaultMessage: 'Blocked only'
	},
	filterNotBlocked: {
		id: 'invoices.organizations.filter.notBlocked',
		defaultMessage: 'Not blocked'
	},
	blockedBadge: {
		id: 'invoices.organizations.blockedBadge',
		defaultMessage: 'BLOCKED'
	},
	// Organizations view filters (inside Organizations tab)
	orgFilterAll: {
		id: 'invoices.organizations.filter.status.all',
		defaultMessage: 'All unpaid'
	},
	orgFilterUnpaid: {
		id: 'invoices.organizations.filter.unpaid',
		defaultMessage: 'Unpaid not overdue'
	},
	orgFilterOverdue: {
		id: 'invoices.organizations.filter.overdue',
		defaultMessage: 'Overdue'
	},
	// Organizations view: overdue period filter (applies only for overdue invoices)
	orgPeriodLabel: {
		id: 'invoices.organizations.filter.periodLabel',
		defaultMessage: 'Overdue period:'
	},
	orgPeriodAll: {
		id: 'invoices.organizations.filter.period.all',
		defaultMessage: 'All'
	},
	orgPeriodUp30: {
		id: 'invoices.organizations.filter.period.up30',
		defaultMessage: 'Up to 30 days'
	},
	orgPeriod31to60: {
		id: 'invoices.organizations.filter.period.31to60',
		defaultMessage: '31–60 days'
	},
	orgPeriod61to90: {
		id: 'invoices.organizations.filter.period.61to90',
		defaultMessage: '61–90 days'
	},
	orgPeriodOver90: {
		id: 'invoices.organizations.filter.period.over90',
		defaultMessage: 'Over 90 days'
	}
});

class OverdueBillsList extends React.Component {
    static propTypes = {
        intl: PropTypes.object,
        onRefresh: PropTypes.func,
        onSelect: PropTypes.func,
        organisationId: PropTypes.string,
        selected: PropTypes.object,
        user: PropTypes.object,
    };
	constructor(props) {
		super(props);
		this.state = {
			items: [],
			expandedOrgGroups: {}, // For showing expanded organization groups {orgId: true/false}
            loading: false,
            error: null,
            selected: null,
			selectedIds: {},
            debug: null,
            notice: null,
            ignoredById: {},
			currentPage: 1,
			pageSize: 50,
            globalSearchResults: null,
            globalSearchError: null,
			// Organizations view state
			viewMode: 'unpaid', // 'all' | 'unpaid' | 'organizations'
			organizationsData: null,
			loadingOrganizations: false,
			globalUnpaidSummary: null, // Deduplicated global summary for banner
			loadingGlobalSummary: false,
			sortBy: 'czk', // 'czk' | 'eur' | 'other' | 'dueDate' | 'invoiceCount'
			sortOrder: 'desc', // 'asc' | 'desc'
			organizationsCurrentPage: 1,
			organizationsPageSize: 50,
			organizationsOverdueFilter: 'all', // 'all' | 'unpaid' | 'overdue' - filter WITHIN Organizations view - DEFAULT: 'all'
			organizationsBlockedFilter: 'all', // 'all' | 'blocked' | 'notBlocked'
			organizationsPeriodFilter: 'all' // 'all' | 'up30' | '31to60' | '61to90' | 'over90' (only meaningful for overdue)
        };
    }

    componentDidUpdate(prevProps) {
        // Trigger global search when searchGlobal is enabled and query changes
        if (this.props.searchGlobal && this.props.searchQuery && this.props.searchQuery.trim() &&
            (prevProps.searchQuery !== this.props.searchQuery || prevProps.searchGlobal !== this.props.searchGlobal)) {
            this.performGlobalSearch(this.props.searchQuery);
        }
        // Clear global results when searchGlobal is disabled or query is cleared
        if ((!this.props.searchGlobal || !this.props.searchQuery) && this.state.globalSearchResults) {
            this.setState({ globalSearchResults: null, globalSearchError: null });
        }
		
		// Sync viewMode with onlyUnpaidOverdue prop (unless in organizations mode)
		if (this.state.viewMode !== 'organizations') {
			const newViewMode = (this.props.onlyUnpaidOverdue !== false) ? 'unpaid' : 'all';
			if (this.state.viewMode !== newViewMode) {
				this.setState({ viewMode: newViewMode });
			}
		}
		
		// Reset organizations page when search query changes in organizations view
		if (this.state.viewMode === 'organizations' && prevProps.searchQuery !== this.props.searchQuery) {
			this.setState({ organizationsCurrentPage: 1 });
		}
    }

    performGlobalSearch = (query) => {
        if (!query || !query.trim()) {
            this.setState({ globalSearchResults: null, globalSearchError: null });
            return;
        }

        this.setState({ loading: true, globalSearchError: null });

        const mutation = new SearchInvoicesGlobalMutation({
            query: query,
            limit: 100
        });

        Relay.Store.commitUpdate(mutation, {
            onSuccess: (response) => {
                this.setState({
                    globalSearchResults: response.searchInvoicesGlobal,
                    loading: false
                });
            },
            onFailure: (transaction) => {
                const error = transaction.getError();
                this.setState({
                    globalSearchError: error ? error.message : 'Search failed',
                    loading: false,
                    globalSearchResults: null
                });
            }
        });
    };

    resolveOrganisationId() {
        if (this.props && this.props.organisationId) return this.props.organisationId;
        try {
            const m = (typeof window !== 'undefined' && window.location && window.location.pathname || '').match(/\/organisations\/([0-9a-fA-F-]{36})\//);
            if (m && m[1]) return m[1];
        } catch (e) {}
        return null;
    }

    async handleLoadInvoicesOrg() {
        this.setState({ loading: true, error: null });
        try {
            this.setState({ notice: { type: 'info', message: this.props.intl ? this.props.intl.formatMessage({ id: 'invoices.jobStarting', defaultMessage: 'Starting job…' }) : 'Starting job…' } });
            let enqueuedJobId = null;
            await new Promise((resolve, reject) => {
                try {
                    Relay.Store.commitUpdate(
                        new EnqueueOverdueBillsFetchJob({ organisationId: this.resolveOrganisationId() }),
                        { onSuccess: (response) => {
                            try {
                                const job = response && response.enqueueOverdueBillsFetchJob && response.enqueueOverdueBillsFetchJob.job;
                                if (job) {
                                    showJobNotification(this.props.intl, job, this.props.intl ? this.props.intl.formatMessage({ id: 'invoices.jobStarted', defaultMessage: 'Fetching invoices…' }) : 'Fetching overdue invoices…');
                                    this.setState({ notice: { type: 'success', message: (this.props.intl ? this.props.intl.formatMessage({ id: 'invoices.jobEnqueued', defaultMessage: 'Job enqueued' }) : 'Job enqueued') + ` (id: ${job.realId || job.id})` } });
                                    enqueuedJobId = job.id || job.realId;
                                } else {
                                    showNotification('Job enqueue', 'Job created');
                                }
                            } catch (e) {}
                            resolve(null);
                        }, onFailure: (_, err) => reject(err) }
                    );
                } catch (e) { reject(e); }
            });
            if (enqueuedJobId) {
                await this.waitForJobFinished(enqueuedJobId);
            }
            if (this.props.onRefresh) {
                this.props.onRefresh();
            } else {
                await this.load();
            }
        } catch (e) {
            this.setState({ error: e && e.message ? e.message : String(e) });
        } finally {
            this.setState({ loading: false });
        }
    }

    async handleLoadInvoicesAll() {
        this.setState({ loading: true, error: null });
        try {
            this.setState({ notice: { type: 'info', message: this.props.intl ? this.props.intl.formatMessage({ id: 'invoices.jobStarting', defaultMessage: 'Starting job…' }) : 'Starting job…' } });
            let enqueuedJobId = null;
            await new Promise((resolve, reject) => {
                try {
                    Relay.Store.commitUpdate(
                        new EnqueueOverdueBillsFetchJob({}),
                        { onSuccess: (response) => {
                            try {
                                const job = response && response.enqueueOverdueBillsFetchJob && response.enqueueOverdueBillsFetchJob.job;
                                if (job) {
                                    showJobNotification(this.props.intl, job, this.props.intl ? this.props.intl.formatMessage({ id: 'invoices.jobStarted', defaultMessage: 'Fetching invoices…' }) : 'Fetching overdue invoices…');
                                    this.setState({ notice: { type: 'success', message: (this.props.intl ? this.props.intl.formatMessage({ id: 'invoices.jobEnqueued', defaultMessage: 'Job enqueued' }) : 'Job enqueued') + ` (id: ${job.realId || job.id})` } });
                                    enqueuedJobId = job.id || job.realId;
                                } else {
                                    showNotification('Job enqueue', 'Job created');
                                }
                            } catch (e) {}
                            resolve(null);
                        }, onFailure: (_, err) => reject(err) }
                    );
                } catch (e) { reject(e); }
            });
            if (enqueuedJobId) {
                await this.waitForJobFinished(enqueuedJobId);
            }
            if (this.props.onRefresh) {
                this.props.onRefresh();
            } else {
                await this.load();
            }
        } catch (e) {
            this.setState({ error: e && e.message ? e.message : String(e) });
        } finally {
            this.setState({ loading: false });
        }
    }

    async handleRunRulesOrg() {
        const msg = this.props.intl ? this.props.intl.formatMessage(messages.confirmNotifyOrg) : 'Are you sure you want to run rules for this organisation?';
        if (!window.confirm(msg)) return;
        try {
            await new Promise((resolve, reject) => {
                try {
                    Relay.Store.commitUpdate(
                        new EnqueueOverdueBillsNotifyJob({ organisationId: this.resolveOrganisationId() }),
                        { onFailure: (_, err) => reject(err), onSuccess: (response) => {
                            try {
                                const job = response && response.enqueueOverdueBillsNotifyJob && response.enqueueOverdueBillsNotifyJob.job;
                                if (job) showJobNotification(this.props.intl, job, 'Running notify for organisation…');
                            } catch (e) {}
                            resolve(null);
                        } }
                    );
                } catch (e) { reject(e); }
            });
        } catch (e) { showNotification('Chyba', String(e && e.message ? e.message : e)); }
    }

    async handleRunRulesAll() {
        const msg = this.props.intl ? this.props.intl.formatMessage(messages.confirmNotifyAll) : 'Are you sure you want to run rules for all organisations?';
        if (!window.confirm(msg)) return;
        try {
            await new Promise((resolve, reject) => {
                try {
                    Relay.Store.commitUpdate(
                        new EnqueueOverdueBillsNotifyJob({}),
                        { onFailure: (_, err) => reject(err), onSuccess: (response) => {
                            try {
                                const job = response && response.enqueueOverdueBillsNotifyJob && response.enqueueOverdueBillsNotifyJob.job;
                                if (job) showJobNotification(this.props.intl, job, 'Running notify for ALL organisations…');
                            } catch (e) {}
                            resolve(null);
                        } }
                    );
                } catch (e) { reject(e); }
            });
        } catch (e) { showNotification('Chyba', String(e && e.message ? e.message : e)); }
    }

    componentDidMount() {
        this.load();
		
		// Initialize viewMode from props (default to 'unpaid' if not explicitly set to false)
		const initialViewMode = (this.props.onlyUnpaidOverdue !== false) ? 'unpaid' : 'all';
		if (this.state.viewMode !== initialViewMode) {
			this.setState({ viewMode: initialViewMode });
		}
    }

    async load() {
        // no-op: data will be provided via Relay parent
        return;
    }

    async waitForJobFinished(jobId, { intervalMs = 1000, attempts = 20 } = {}) {
        if (!jobId) return false;
        const endpoint = '/?locale=cs';
        const query = `query Q($jobId: ID!) { user { job(id: $jobId) { status } } }`;
        for (let i = 0; i < attempts; i++) {
            try {
                const resp = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ query, variables: { jobId } }),
                });
                const json = await resp.json();
                const status = json && json.data && json.data.user && json.data.user.job && json.data.user.job.status;
                if (status === 'finished' || status === 'failed' || status === 'skipped') return true;
            } catch (e) { /* ignore and retry */ }
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
        return false;
    }

    async handleDelete(item) {
        const id = item && item.id;
        if (!id) return;
        const code = item && item.code ? String(item.code) : '';
        if (!window.confirm(`Opravdu smazat doklad ${code || id}?`)) return;
        await new Promise((resolve, reject) => {
            try {
                Relay.Store.commitUpdate(new DeleteOrganisationOverdueBill({ id }), {
                    onSuccess: () => resolve(null),
                    onFailure: (_, err) => reject(err),
                });
            } catch (e) { reject(e); }
        });
        // After deletion, request parent (Relay) refresh via props.onRefresh if it exists
        if (this.props.onRefresh) this.props.onRefresh();
    }

	handleToggleSelect(id, checked) {
		this.setState((prev) => ({
			selectedIds: { ...prev.selectedIds, [id]: !!checked }
		}));
	}

	handleToggleSelectAll(items, checked) {
		const next = {};
		if (checked) {
			for (const it of items) next[it.id] = true;
		}
		this.setState({ selectedIds: next });
	}

	async handleBulkDelete(items) {
		const ids = items.filter((it) => this.state.selectedIds[it.id]).map((it) => it.id);
		if (ids.length === 0) return;
		const msg = this.props.intl ? this.props.intl.formatMessage(messages.confirmDeleteSelected) : 'Delete selected invoices?';
		if (!window.confirm(msg)) return;
		for (const id of ids) {
			// eslint-disable-next-line no-await-in-loop
			await new Promise((resolve) => {
				try {
					Relay.Store.commitUpdate(new DeleteOrganisationOverdueBill({ id }), {
						onSuccess: () => resolve(null),
						onFailure: () => resolve(null),
					});
				} catch (e) { resolve(null); }
			});
		}
		this.setState({ selectedIds: {} });
		if (this.props.onRefresh) this.props.onRefresh();
	}

	async handleBulkUnignore(items) {
		const selectedItems = items.filter((it) => this.state.selectedIds[it.id]);
		if (selectedItems.length === 0) return;
		
		// Check if any selected item is ignored by label
		const labelIgnored = selectedItems.filter((it) => it.ignoreSource === 'label');
		if (labelIgnored.length > 0) {
			const msg = this.props.intl.formatMessage(messages.cannotChangeLabel);
			showNotification('Warning', msg);
			return;
		}
		
		const ids = selectedItems.map((it) => it.id);
		const msg = this.props.intl ? this.props.intl.formatMessage(messages.confirmUnignoreSelected) : 'Remove ignore flag for selected invoices?';
		if (!window.confirm(msg)) return;
		// optimistic UI
		this.setState((prev) => {
			const next = { ...prev.ignoredById };
			for (const id of ids) next[id] = false;
			return { ignoredById: next };
		});
		for (const id of ids) {
			// eslint-disable-next-line no-await-in-loop
			await new Promise((resolve) => {
				try {
					Relay.Store.commitUpdate(new UpdateOrganisationOverdueBillIgnore({ id, isIgnored: false, ignoreSource: null }), {
						onSuccess: () => resolve(null),
						onFailure: () => resolve(null),
					});
				} catch (e) { resolve(null); }
			});
		}
		this.setState({ selectedIds: {} });
		if (this.props.onRefresh) this.props.onRefresh();
	}

	async handleBulkIgnore(items) {
		const selectedItems = items.filter((it) => this.state.selectedIds[it.id]);
		if (selectedItems.length === 0) return;
		
		// Filter out items that are already ignored by label (cannot be re-ignored manually)
		const labelIgnored = selectedItems.filter((it) => it.ignoreSource === 'label');
		if (labelIgnored.length > 0) {
			const msg = this.props.intl.formatMessage(messages.cannotChangeLabel);
			showNotification('Warning', msg);
			return;
		}
		
		const ids = selectedItems.map((it) => it.id);
		const msg = this.props.intl ? this.props.intl.formatMessage(messages.confirmIgnoreSelected) : 'Ignore selected invoices in rules?';
		if (!window.confirm(msg)) return;
		// optimistic UI
		this.setState((prev) => {
			const next = { ...prev.ignoredById };
			for (const id of ids) next[id] = true;
			return { ignoredById: next };
		});
		for (const id of ids) {
			// eslint-disable-next-line no-await-in-loop
			await new Promise((resolve) => {
				try {
					Relay.Store.commitUpdate(new UpdateOrganisationOverdueBillIgnore({ id, isIgnored: true, ignoreSource: 'manual' }), {
						onSuccess: () => resolve(null),
						onFailure: () => resolve(null),
					});
				} catch (e) { resolve(null); }
			});
		}
		this.setState({ selectedIds: {} });
		if (this.props.onRefresh) this.props.onRefresh();
	}

	renderIgnoredBadge = (item) => {
		if (!item.isIgnored) return null;
		
		const { intl } = this.props;
		const source = item.ignoreSource;
		
		if (source === 'label') {
			// Ignored by FlexiBee label - blue label
			return (
				<div>
					<span 
						className="label label-info" 
						title={intl.formatMessage(messages.ignoredByLabelTooltip)}
						style={{ fontSize: '11px' }}
					>
						<i className="fa fa-tag" style={{ marginRight: '3px' }}></i>
						<FormattedMessage {...messages.ignoredByLabel} />
					</span>
				</div>
			);
		} else if (source === 'manual') {
			// Manually ignored - orange label
			return (
				<div>
					<span 
						className="label label-warning" 
						title={intl.formatMessage(messages.ignoredManuallyTooltip)}
						style={{ fontSize: '11px' }}
					>
						<i className="fa fa-hand-paper-o" style={{ marginRight: '3px' }}></i>
						<FormattedMessage {...messages.ignoredManually} />
					</span>
				</div>
			);
		} else {
			// Legacy ignored (no source) - gray label
			return (
				<div>
					<span 
						className="label label-default" 
						title="Ignored (legacy)"
						style={{ fontSize: '11px' }}
					>
						<i className="fa fa-ban" style={{ marginRight: '3px' }}></i>
						Ignored
					</span>
				</div>
			);
		}
	};

    renderRow(item) {
        const toText = (v) => {
            if (v == null) return '';
            if (typeof v === 'object') return JSON.stringify(v);
            return String(v);
        };
        const formatCzechNumber = (value) => {
            if (value == null || value === '') return '';
            const num = Number(value);
            if (Number.isNaN(num)) return String(value);
            // Format to 2 decimal places
            const formatted = num.toFixed(2);
            const [integerPart, decimalPart] = formatted.split('.');
            // Add spaces as thousands separator
            const withSpaces = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
            // Use comma as decimal separator
            return `${withSpaces},${decimalPart}`;
        };
        const handleDownload = (format) => {
            try {
                Relay.Store.commitUpdate(new DownloadOrganisationOverdueBillDocument({ id: item.id, format }), {
                    onSuccess: (res) => {
                        try {
                            const url = res && res.downloadOrganisationOverdueBillDocument && res.downloadOrganisationOverdueBillDocument.url;
                            if (url) {
                                window.open(url, '_blank');
                            } else {
                                showNotification('Download', 'Download failed (no URL returned).');
                            }
                        } catch (e) {
                            showNotification('Download', 'Download failed.');
                        }
                    },
                    onFailure: () => {
                        showNotification('Download', 'Download failed.');
                    },
                });
            } catch (e) {
                showNotification('Download', 'Download failed.');
            }
        };
        let parsedDate;
        if (item.dueDate && typeof item.dueDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(item.dueDate)) {
            parsedDate = moment(item.dueDate, 'YYYY-MM-DD');
        } else if (item.dueDate) {
            parsedDate = moment(item.dueDate);
        }
        // Use startOf('day') to compare calendar days, not 24-hour periods (consistent with backend)
        const daysOverdue = parsedDate && parsedDate.isValid() ? moment().startOf('day').diff(parsedDate.startOf('day'), 'days') : undefined;
        // determine developer visibility
        const organisationIdForRow = this.resolveOrganisationId();
        const isDeveloperForRow = organisationIdForRow
            ? (BrowserPermission.hasOrganisationPermission(Permission.PERM_DEVELOPER, organisationIdForRow) && BrowserPermission.developerModeEnabled(organisationIdForRow))
            : false;
        const isPartner2ForRow = organisationIdForRow ? BrowserPermission.isPartner2(organisationIdForRow) : false;
        const isPartner1ForRow = organisationIdForRow ? BrowserPermission.isPartner1(organisationIdForRow) : false;
        const canSelectRow = !!(isDeveloperForRow || isPartner1ForRow || isPartner2ForRow);
        const isIgnored = this.state.ignoredById[item.id] != null ? !!this.state.ignoredById[item.id] : !!item.isIgnored;
        const toNumberOrNull = (v) => {
            if (v === null || v === undefined) return null;
            const n = Number(v);
            return Number.isNaN(n) ? null : n;
        };
        const amountToPayNum = toNumberOrNull(item.amountToPay);
        const totalAmountNum = toNumberOrNull(item.totalAmount);
        const isPaid = amountToPayNum != null ? amountToPayNum <= 0 : false;
        const isPartial = amountToPayNum != null && totalAmountNum != null ? (amountToPayNum > 0 && amountToPayNum < totalAmountNum) : false;
        const isOverdueUnpaid = amountToPayNum != null ? (amountToPayNum > 0 && (daysOverdue != null && daysOverdue > 0)) : false;
        let statusClass = '';
        if (isPaid) statusClass = 'status-paid';
        else if (isPartial) statusClass = 'status-partial';
        else if (isOverdueUnpaid) statusClass = 'status-overdue';
        // Check if this row is selected
        const selectedItem = this.props.selected || this.state.selected;
        const isSelected = selectedItem && (selectedItem.id === item.id || selectedItem.realId === item.realId);
        const selectedClass = isSelected ? 'selected-row' : '';
        // Build row style
        let rowStyle = {};
        if (isIgnored && isDeveloperForRow) {
            rowStyle.opacity = 0.6;
        }
        if (isSelected) {
            rowStyle.backgroundColor = '#e8f4f8';
            rowStyle.borderLeft = '3px solid #0088cc';
        }
        return (
			<tr className={`${canSelectRow ? 'clickable-row ' : ''}${statusClass} ${selectedClass}`} key={String(item.id || item.code || Math.random())} onClick={canSelectRow ? (() => {
                // If this is a bill from another organization (global search result), open in new tab
                if (item.__organisation && item.__organisation.id) {
                    window.open(`/organisations/${item.__organisation.id}/invoices/?invoice=${item.id}`, '_blank');
                    return;
                }
                // Normal local select
                if (this.props.onSelect) {
                    this.props.onSelect(item.__expanded || item);
                } else {
                    this.setState({ selected: (item.__expanded || item) });
                }
			}) : undefined} role={canSelectRow ? 'button' : undefined} style={rowStyle} title={canSelectRow ? (item.__organisation ? `Otevřít detail v novém okně (organizace: ${item.__organisation.name})` : "Zobrazit detail") : undefined}>
				{isDeveloperForRow ? (
					<td onClick={(e) => e.stopPropagation()} style={{width: 28, textAlign: 'center', verticalAlign: 'middle'}}>
					<input
						checked={!!this.state.selectedIds[item.id]}
						onChange={(e) => this.handleToggleSelect(item.id, !!e.target.checked)}
						type="checkbox"
					/>
				</td>
				) : null}
                {this.props.searchGlobal && this.state.globalSearchResults && item.__organisation ? (
                    <td style={{textAlign:'left', verticalAlign: 'middle'}}>
                        <strong>{toText(item.__organisation.name)}</strong>
                        <i className="fa fa-external-link" style={{ marginLeft: 6, fontSize: 11, color: '#999' }}></i>
                    </td>
                ) : null}
                <td style={{textAlign:'center', verticalAlign: 'middle'}}>
                    <div>{toText(item.code)}</div>
                    {isIgnored && isDeveloperForRow ? this.renderIgnoredBadge(item) : null}
                    {item.isAdvancePayment ? (
                        <div>
                            <span 
                                className="label label-warning" 
                                title={this.props.intl.formatMessage(messages.advancePaymentTooltip)}
                                style={{ fontSize: '11px', fontWeight: 'bold' }}
                            >
                                ⚡ <FormattedMessage {...messages.advancePayment} />
                            </span>
                        </div>
                    ) : null}
                </td>
                <td style={{textAlign:'center', verticalAlign: 'middle'}}>{toText(item.variableSymbol)}</td>
                <td style={{textAlign:'center', verticalAlign: 'middle'}}>{parsedDate && parsedDate.isValid() ? parsedDate.format('DD.MM.YYYY') : ''}</td>
                <td style={{textAlign:'right', verticalAlign: 'middle', fontFamily: 'monospace'}}>
                    {formatCzechNumber(item.totalAmount)} <small style={{ color: '#999' }}>{toText(item.currencyCode)}</small>
                </td>
                <td style={{textAlign:'right', verticalAlign: 'middle', fontFamily: 'monospace'}}>
                    {formatCzechNumber(item.amountToPay)} <small style={{ color: '#999' }}>{toText(item.currencyCode)}</small>
                </td>
                <td style={{textAlign:'center', verticalAlign: 'middle', display: 'none'}}>{toText(item.currencyCode)}</td>
                <td style={{textAlign:'center', verticalAlign: 'middle'}}>{!isPaid && daysOverdue != null && daysOverdue > 0 ? String(daysOverdue) : ''}</td>
                <td onClick={(e) => e.stopPropagation()} style={{textAlign:'center', verticalAlign: 'middle'}}>
                    <button
                        className="btn btn-default btn-xs"
                        onClick={(e) => { e.stopPropagation(); handleDownload('pdf'); }}
                        style={{ marginRight: 6 }}
                        title="PDF"
                        type="button"
                    >
                        <span aria-hidden="true" className="fa fa-file-pdf-o" style={{ color: '#d9534f' }}></span>
                    </button>
                    <button
                        className="btn btn-default btn-xs"
                        onClick={(e) => { e.stopPropagation(); handleDownload('isdoc'); }}
                        title="ISDOC"
                        type="button"
                    >
                        <span aria-hidden="true" className="isdoc-icon"></span>
                    </button>
                </td>
            </tr>
        );
    }

	// ========== Organizations View Methods ==========
	
	loadOrganizationsView = () => {
		this.setState({ 
			loadingOrganizations: true,
			loadingGlobalSummary: true 
		});
		
		// Load organizations list (per-org breakdown for table)
		const orgMutation = new GetOrganizationsWithUnpaidInvoices({});
		
		Relay.Store.commitUpdate(orgMutation, {
			onSuccess: (response) => {
				// Try multiple possible response structures
				let organizations = [];
				if (response && response.getOrganizationsWithUnpaidInvoices) {
					if (response.getOrganizationsWithUnpaidInvoices.organizations) {
						// mutationWithClientMutationId with outputFields
						organizations = response.getOrganizationsWithUnpaidInvoices.organizations;
					} else if (Array.isArray(response.getOrganizationsWithUnpaidInvoices)) {
						// Direct array
						organizations = response.getOrganizationsWithUnpaidInvoices;
					}
				} else if (response && response.organizations) {
					organizations = response.organizations;
				}
				
				this.setState({
					organizationsData: organizations,
					loadingOrganizations: false
				});
			},
			onFailure: (transaction) => {
				console.error('[DEBUG] Failed to load organizations:', transaction.getError());
				console.error('[DEBUG] Transaction:', transaction);
				showNotification('Failed to load organizations', 'error');
				this.setState({ loadingOrganizations: false });
			}
		});
		
		// Load deduplicated global summary (for banner)
		const summaryMutation = new GetGlobalUnpaidInvoiceSummary();
		
		Relay.Store.commitUpdate(summaryMutation, {
			onSuccess: (response) => {
				const summary = response && response.getGlobalUnpaidInvoiceSummary 
					? response.getGlobalUnpaidInvoiceSummary 
					: null;
				
				this.setState({
					globalUnpaidSummary: summary,
					loadingGlobalSummary: false
				});
			},
			onFailure: (transaction) => {
				console.error('Failed to load global summary:', transaction.getError());
				// Don't show notification - banner will just use fallback calculation
				this.setState({ 
					loadingGlobalSummary: false,
					globalUnpaidSummary: null 
				});
			}
		});
	};
	
	handleSort = (field) => {
		const { sortBy, sortOrder } = this.state;
		
		// Toggle order if same field, otherwise default to desc
		const newOrder = sortBy === field && sortOrder === 'desc' ? 'asc' : 'desc';
		
		this.setState({
			sortBy: field,
			sortOrder: newOrder,
			organizationsCurrentPage: 1 // Reset to first page when sorting changes
		});
	};

	getOrganizationAmount = (org, currency, overdueFilter, periodFilter) => {
		if (!org) return 0;
		let baseField = '';
		// Period filter is an additional filter that applies only when a concrete bucket is selected.
		// Period "all" means "no period filter".
		if (periodFilter && periodFilter !== 'all') {
			// Buckets are computed with "days past due" where NOT overdue invoices are treated as 0 days.
			// So:
			// - Status=all: buckets partition ALL unpaid (sum of buckets == totalUnpaid)
			// - Status=unpaid not overdue: only bucket up30 can contain data (0 days)
			// - Status=overdue: bucket up30 must exclude not-overdue part (subtract it)
			if (overdueFilter === 'unpaid') {
				return periodFilter === 'up30' ? Number(org[`totalNotOverdue${currency}`] || 0) : 0;
			}
			if (periodFilter === 'up30') baseField = 'totalUp30';
			else if (periodFilter === '31to60') baseField = 'total31to60';
			else if (periodFilter === '61to90') baseField = 'total61to90';
			else baseField = 'totalOver90';
			let val = Number(org[`${baseField}${currency}`] || 0);
			if (overdueFilter === 'overdue' && periodFilter === 'up30') {
				val -= Number(org[`totalNotOverdue${currency}`] || 0);
			}
			return val;
		} else if (overdueFilter === 'overdue') {
			baseField = 'totalOverdue';
		} else if (overdueFilter === 'unpaid') {
			baseField = 'totalNotOverdue';
		} else {
			baseField = 'totalUnpaid';
		}
		const fullField = `${baseField}${currency}`;
		return Number(org[fullField] || 0);
	};

	getOrganizationCount = (org, overdueFilter, periodFilter) => {
		if (!org) return 0;
		if (periodFilter && periodFilter !== 'all') {
			if (overdueFilter === 'unpaid') {
				return periodFilter === 'up30' ? Number(org.invoiceCountNotOverdue || 0) : 0;
			}
			if (periodFilter === 'up30') return Number(org.invoiceCountUp30 || 0);
			if (periodFilter === '31to60') return Number(org.invoiceCount31to60 || 0);
			if (periodFilter === '61to90') return Number(org.invoiceCount61to90 || 0);
			return Number(org.invoiceCountOver90 || 0);
		}
		if (overdueFilter === 'overdue') return Number(org.invoiceCountOverdue || 0);
		if (overdueFilter === 'unpaid') return Number(org.invoiceCountNotOverdue || 0);
		return Number(org.invoiceCount || 0);
	};
	
	sortOrganizations(data, sortBy, sortOrder, overdueFilter, periodFilter) {
		const sorted = [...data];
		
		sorted.sort((a, b) => {
			let aValue, bValue;
			
			switch(sortBy) {
				case 'invoiceCount':
					aValue = this.getOrganizationCount(a, overdueFilter, periodFilter);
					bValue = this.getOrganizationCount(b, overdueFilter, periodFilter);
					break;
				case 'czk':
					aValue = this.getOrganizationAmount(a, 'CZK', overdueFilter, periodFilter);
					bValue = this.getOrganizationAmount(b, 'CZK', overdueFilter, periodFilter);
					break;
				case 'eur':
					aValue = this.getOrganizationAmount(a, 'EUR', overdueFilter, periodFilter);
					bValue = this.getOrganizationAmount(b, 'EUR', overdueFilter, periodFilter);
					break;
				case 'other':
					aValue = this.getOrganizationAmount(a, 'Other', overdueFilter, periodFilter);
					bValue = this.getOrganizationAmount(b, 'Other', overdueFilter, periodFilter);
					break;
				case 'dueDate':
					// Sort by oldest due date (oldest first for asc, newest first for desc)
					aValue = a.oldestDueDate ? moment(a.oldestDueDate).valueOf() : Infinity;
					bValue = b.oldestDueDate ? moment(b.oldestDueDate).valueOf() : Infinity;
					break;
				default:
					return 0;
			}
			
			if (sortOrder === 'desc') {
				return bValue - aValue;
			} else {
				return aValue - bValue;
			}
		});
		
		return sorted;
	}
	
	formatCzechNumber = (number) => {
		if (number === null || number === undefined) return '—';
		const num = parseFloat(number);
		if (isNaN(num)) return '—';
		// Czech formatting: space as thousand separator, comma as decimal
		return num.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&\u00A0').replace('.', ',');
	};
	
	handleOrganizationClick = (orgId) => {
		// Navigate to organization's invoices list
		window.location.href = `/organisations/${orgId}/invoices/`;
	};
	
	renderSortableHeader(field, message, align) {
		const { sortBy, sortOrder } = this.state;
		const isActive = sortBy === field;
		
		const getSortIcon = () => {
			if (!isActive) return 'fa-sort';
			return sortOrder === 'desc' ? 'fa-sort-down' : 'fa-sort-up';
		};
		
		const headerStyle = {
			textAlign: align,
			cursor: 'pointer',
			userSelect: 'none',
			backgroundColor: isActive ? '#e3f2fd' : undefined,
			color: isActive ? '#1976d2' : undefined,
			transition: 'background-color 0.2s'
		};
		
		return (
			<th 
				style={headerStyle}
				onClick={() => this.handleSort(field)}
				className={isActive ? 'sortable active' : 'sortable'}
			>
				<FormattedMessage {...message} />
				<i 
					className={`fa ${getSortIcon()}`} 
					style={{ 
						marginLeft: 5, 
						fontSize: 11,
						opacity: isActive ? 1 : 0.3 
					}} 
				/>
			</th>
		);
	}
	
	renderExpandedOrganizationRows(org) {
		if (!org.organizationsInGroup || org.organizationsInGroup.length <= 1) return null;
		
		// Filter out the primary organization from the list (already shown in main row)
		const otherOrgs = org.organizationsInGroup.filter(o => o.organisationId !== org.organisationId);
		
		if (otherOrgs.length === 0) return null;
		
		const { organizationsOverdueFilter, organizationsPeriodFilter } = this.state;
		
		return otherOrgs.map((o, idx) => {
			const czkAmount = this.getOrganizationAmount(o, 'CZK', organizationsOverdueFilter, organizationsPeriodFilter);
			const eurAmount = this.getOrganizationAmount(o, 'EUR', organizationsOverdueFilter, organizationsPeriodFilter);
			const otherAmount = this.getOrganizationAmount(o, 'Other', organizationsOverdueFilter, organizationsPeriodFilter);
			const invoiceCountDisplay = this.getOrganizationCount(o, organizationsOverdueFilter, organizationsPeriodFilter);
			
			const hasCZK = czkAmount > 0;
			const hasEUR = eurAmount > 0;
			const hasOther = otherAmount > 0;
			
			// Select relevant due date
			let relevantDueDate;
			if (organizationsOverdueFilter === 'overdue') {
				relevantDueDate = o.oldestDueDateOverdue;
			} else if (organizationsOverdueFilter === 'unpaid') {
				relevantDueDate = o.oldestDueDateNotOverdue;
			} else {
				relevantDueDate = o.oldestDueDate;
			}
			
			const formattedDate = relevantDueDate 
				? moment(relevantDueDate).format('DD.MM.YYYY')
				: '—';
			
			const daysOverdue = relevantDueDate
				? moment().diff(moment(relevantDueDate), 'days')
				: 0;
			
			return (
				<tr 
					key={`${org.organisationId}-sub-${idx}`}
					style={{ 
						backgroundColor: '#f9f9f9',
						cursor: 'pointer'
					}}
					onClick={() => window.open(`/organisations/${o.organisationId}/invoices/`, '_blank')}
				>
					<td style={{ textAlign: 'left', paddingLeft: 40 }}>
						<i className="fa fa-level-up fa-rotate-90" style={{ marginRight: 8, color: '#999' }}></i>
						{o.organisationName}
						{typeof o.invoiceCompanyName === 'string' &&
							o.invoiceCompanyName.trim() && (
								<span style={{ marginLeft: 8, color: '#777' }}>
									— {o.invoiceCompanyName.trim()}
								</span>
							)}
						<span style={{ marginLeft: 8, fontSize: 11, color: '#999', fontStyle: 'italic' }}>
							(sdílí IČ: {org.registrationNumber || 'neuvedeno'})
						</span>
						{o.isBlocked && (
							<span className="label label-danger" style={{ marginLeft: 10, fontSize: 10 }}>
								<i className="fa fa-ban" style={{ marginRight: 4 }}></i>
								<FormattedMessage {...messages.blockedBadge} />
							</span>
						)}
					</td>
					<td style={{ textAlign: 'center' }}>
						<span className="badge" style={{ backgroundColor: '#999', color: 'white' }}>
							{invoiceCountDisplay}
						</span>
					</td>
					<td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
						{hasCZK ? <span>{this.formatCzechNumber(czkAmount)} <small style={{ color: '#999' }}>CZK</small></span> : '—'}
					</td>
					<td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
						{hasEUR ? <span>{this.formatCzechNumber(eurAmount)} <small style={{ color: '#999' }}>EUR</small></span> : '—'}
					</td>
					<td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
						{hasOther ? (
							<span>
								{this.formatCzechNumber(otherAmount)} <small style={{ color: '#999' }}>{o.totalUnpaidOtherCurrency || 'OTHER'}</small>
							</span>
						) : '—'}
					</td>
					<td style={{ textAlign: 'center' }}>
						<div>{formattedDate}</div>
						{daysOverdue > 0 && (
							<div style={{ 
								fontSize: 11, 
								marginTop: 3,
								color: daysOverdue > 30 ? '#d32f2f' : daysOverdue > 10 ? '#f57c00' : '#666',
								fontWeight: daysOverdue > 10 ? 600 : 'normal'
							}}>
								<i className={daysOverdue > 30 ? 'fa fa-exclamation-triangle' : 'fa fa-clock-o'}></i> +{daysOverdue} {daysOverdue === 1 ? 'den' : daysOverdue < 5 ? 'dny' : 'dní'}
							</div>
						)}
					</td>
				</tr>
			);
		});
	}

	renderOrganizationRow(org) {
		const { organisationId } = this.props;
		const { organizationsOverdueFilter, organizationsPeriodFilter } = this.state;
		
		const czkAmount = this.getOrganizationAmount(org, 'CZK', organizationsOverdueFilter, organizationsPeriodFilter);
		const eurAmount = this.getOrganizationAmount(org, 'EUR', organizationsOverdueFilter, organizationsPeriodFilter);
		const otherAmount = this.getOrganizationAmount(org, 'Other', organizationsOverdueFilter, organizationsPeriodFilter);
		const invoiceCountDisplay = this.getOrganizationCount(org, organizationsOverdueFilter, organizationsPeriodFilter);
		
		const hasCZK = czkAmount > 0;
		const hasEUR = eurAmount > 0;
		const hasOther = otherAmount > 0;
		
		// Select appropriate oldest due date based on active filter
		let relevantDueDate = null;
		
		if (organizationsOverdueFilter === 'overdue') {
			relevantDueDate = org.oldestDueDateOverdue;
		} else if (organizationsOverdueFilter === 'unpaid') {
			relevantDueDate = org.oldestDueDateNotOverdue;
		} else {
			// 'all' - use overall oldest due date
			relevantDueDate = org.oldestDueDate;
		}
		
		// Format date
		const formattedDate = relevantDueDate 
			? moment(relevantDueDate).format('DD.MM.YYYY')
			: '—';
		
		// Calculate days overdue for the relevant invoice
		const daysOverdue = relevantDueDate
			? moment().diff(moment(relevantDueDate), 'days')
			: 0;
		
		// Build organization invoices URL
		const orgUrl = `/organisations/${org.organisationId}/invoices/`;
		
		const isExpanded = this.state.expandedOrgGroups[org.organisationId];
		
		return (
			<tr
				style={{ cursor: 'pointer' }}
				onClick={() => window.open(orgUrl, '_blank')}
			>
				<td style={{ textAlign: 'left' }}>
					{org.organizationCount > 1 && (
						<i 
							className={`fa fa-${isExpanded ? 'chevron-down' : 'chevron-right'}`}
							style={{ 
								marginRight: 8, 
								cursor: 'pointer',
								color: '#337ab7',
								fontSize: 14,
								width: 14,
								display: 'inline-block'
							}}
							onClick={(e) => {
								e.stopPropagation();
								this.setState({
									expandedOrgGroups: {
										...this.state.expandedOrgGroups,
										[org.organisationId]: !isExpanded
									}
								});
							}}
						/>
					)}
					{org.organizationCount === 1 && (
						<span style={{ marginRight: 8, width: 14, display: 'inline-block' }}></span>
					)}
					<strong>{org.organisationName}</strong>
					{typeof org.invoiceCompanyName === 'string' &&
						org.invoiceCompanyName.trim() && (
							<span style={{ marginLeft: 8, color: '#777', fontWeight: 'normal' }}>
								— {org.invoiceCompanyName.trim()}
							</span>
						)}
					{org.organizationCount > 1 && (
						<span className="label label-default" style={{ marginLeft: 10 }}>
							+{org.organizationCount - 1}
						</span>
					)}
					{org.organisationId === organisationId && (
						<span className="label label-info" style={{ marginLeft: 10 }}>
							Current
						</span>
					)}
					{org.isBlocked && (
						<span className="label label-danger" style={{ marginLeft: 10 }}>
							<i className="fa fa-ban" style={{ marginRight: 4 }}></i>
							<FormattedMessage {...messages.blockedBadge} />
						</span>
					)}
				</td>
				<td style={{ textAlign: 'center' }}>
					<span className="badge" style={{ backgroundColor: '#f0ad4e', color: 'white' }}>
						{invoiceCountDisplay}
					</span>
				</td>
				<td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
					{hasCZK ? <span>{this.formatCzechNumber(czkAmount)} <small style={{ color: '#999' }}>CZK</small></span> : '—'}
				</td>
				<td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
					{hasEUR ? <span>{this.formatCzechNumber(eurAmount)} <small style={{ color: '#999' }}>EUR</small></span> : '—'}
				</td>
				<td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
					{hasOther ? (
						<span>
							{this.formatCzechNumber(otherAmount)} <small style={{ color: '#999' }}>{org.totalUnpaidOtherCurrency || 'OTHER'}</small>
						</span>
					) : '—'}
				</td>
				<td style={{ textAlign: 'center' }}>
					<div>{formattedDate}</div>
					{daysOverdue > 0 && (
						<div style={{ 
							fontSize: 11, 
							marginTop: 3,
							color: daysOverdue > 30 ? '#d32f2f' : daysOverdue > 10 ? '#f57c00' : '#666',
							fontWeight: daysOverdue > 10 ? 600 : 'normal'
						}}>
							<i className={daysOverdue > 30 ? 'fa fa-exclamation-triangle' : 'fa fa-clock-o'}></i> +{daysOverdue} {daysOverdue === 1 ? 'den' : daysOverdue < 5 ? 'dny' : 'dní'}
						</div>
					)}
				</td>
			</tr>
		);
	}
	
	/**
	 * Filter organizations data based on active filters
	 * Returns filtered and grouped organizations ready for display
	 */
	getFilteredOrganizationsData(organizationsData, organizationsBlockedFilter, organizationsOverdueFilter, searchQuery) {
		if (!organizationsData || organizationsData.length === 0) {
			return [];
		}
		const periodFilter = this.state.organizationsPeriodFilter;
		
		// Group organizations by registrationNumber (IČ)
		// Backend returns each org separately, we need to group them for display
		const groupedOrgs = {};
		organizationsData.forEach(org => {
			const groupKey = org.registrationNumber || org.organisationId;
			if (!groupedOrgs[groupKey]) {
				groupedOrgs[groupKey] = [];
			}
			groupedOrgs[groupKey].push(org);
		});
		
		// Convert to array with primary org selected based on filters
		let filteredData = Object.values(groupedOrgs).map(orgsInGroup => {
			// First, filter orgs in group by blocked status if filter is active
			let orgsMatchingBlockedFilter = orgsInGroup;
			if (organizationsBlockedFilter === 'blocked') {
				orgsMatchingBlockedFilter = orgsInGroup.filter(o => o.isBlocked === true);
			} else if (organizationsBlockedFilter === 'notBlocked') {
				orgsMatchingBlockedFilter = orgsInGroup.filter(o => o.isBlocked !== true);
			}
			
			// If no orgs match the blocked filter, skip this group
			if (orgsMatchingBlockedFilter.length === 0) {
				return null;
			}
			
			// Sort by relevant CZK amount (based on active filters) to choose primary
			orgsMatchingBlockedFilter.sort((a, b) => {
				const aVal = this.getOrganizationAmount(a, 'CZK', organizationsOverdueFilter, periodFilter);
				const bVal = this.getOrganizationAmount(b, 'CZK', organizationsOverdueFilter, periodFilter);
				return bVal - aVal;
			});
			const primary = orgsMatchingBlockedFilter[0];
			
			// Add group metadata to primary
			// organizationsInGroup contains ONLY orgs that match the blocked filter
			return {
				...primary,
				organizationCount: orgsMatchingBlockedFilter.length,
				organizationsInGroup: orgsMatchingBlockedFilter // Only orgs matching the filter
			};
		}).filter(Boolean); // Remove null entries
		
		// Filter organizations by status (and optionally period)
		if (organizationsOverdueFilter === 'overdue') {
			filteredData = filteredData.filter(org => {
				const hasOverdueAmount =
					(this.getOrganizationAmount(org, 'CZK', 'overdue', periodFilter) > 0) ||
					(this.getOrganizationAmount(org, 'EUR', 'overdue', periodFilter) > 0) ||
					(this.getOrganizationAmount(org, 'Other', 'overdue', periodFilter) > 0);
				return hasOverdueAmount;
			});
		} else if (organizationsOverdueFilter === 'unpaid') {
			// Period buckets are overdue-only -> intersection with not-overdue is empty.
			if (periodFilter && periodFilter !== 'all') {
				filteredData = [];
			} else {
			filteredData = filteredData.filter(org => {
				const hasNotOverdueAmount =
					(this.getOrganizationAmount(org, 'CZK', 'unpaid', 'all') > 0) ||
					(this.getOrganizationAmount(org, 'EUR', 'unpaid', 'all') > 0) ||
					(this.getOrganizationAmount(org, 'Other', 'unpaid', 'all') > 0);
				return hasNotOverdueAmount;
			});
			}
		} else if (periodFilter && periodFilter !== 'all') {
			// Status = All unpaid, but concrete period bucket selected => filter to that overdue bucket.
			filteredData = filteredData.filter(org => {
				const hasBucketAmount =
					(this.getOrganizationAmount(org, 'CZK', 'all', periodFilter) > 0) ||
					(this.getOrganizationAmount(org, 'EUR', 'all', periodFilter) > 0) ||
					(this.getOrganizationAmount(org, 'Other', 'all', periodFilter) > 0);
				return hasBucketAmount;
			});
		}
		// organizationsOverdueFilter === 'all' shows all organizations with unpaid invoices (unless period filter is active above)
		
		// Filter organizations by blocked status
		if (organizationsBlockedFilter === 'blocked') {
			filteredData = filteredData.filter(org => org.isBlocked === true);
		} else if (organizationsBlockedFilter === 'notBlocked') {
			filteredData = filteredData.filter(org => org.isBlocked !== true);
		}
		
		// Filter organizations by search query (case-insensitive)
		if (searchQuery && searchQuery.trim()) {
			const searchLower = searchQuery.trim().toLowerCase();
			filteredData = filteredData.filter(org => {
				const orgName = (org.organisationName || '').toLowerCase();
				const invoiceCompanyName = (org.invoiceCompanyName || '').toLowerCase();
				return orgName.includes(searchLower) || invoiceCompanyName.includes(searchLower);
			});
		}
		
		return filteredData;
	}

	renderOrganizationsTable() {
		const { organizationsData, loadingOrganizations, sortBy, sortOrder, organizationsBlockedFilter, organizationsOverdueFilter, organizationsPeriodFilter } = this.state;
		const { intl } = this.props;
		
		if (loadingOrganizations) {
			return (
				<div style={{ padding: 60, textAlign: 'center' }}>
					<i className="fa fa-spinner fa-spin" style={{ fontSize: 48, color: '#337ab7' }} />
					<p style={{ marginTop: 15, color: '#666' }}>{intl.formatMessage(messages.loadingOrganizations)}</p>
				</div>
			);
		}
		
		if (!organizationsData || organizationsData.length === 0) {
			return (
				<div style={{ padding: 60, textAlign: 'center' }}>
					<p style={{ color: '#666' }}>{intl.formatMessage(messages.noOrganizationsFound)}</p>
				</div>
			);
		}
		
		// Get filtered data using centralized method
		const searchQuery = this.props.searchQuery;
		const filteredData = this.getFilteredOrganizationsData(
			organizationsData,
			organizationsBlockedFilter,
			organizationsOverdueFilter,
			searchQuery
		);
		
		// Show "no results" if filtered data is empty
		if (filteredData.length === 0) {
			return (
				<div style={{ padding: 60, textAlign: 'center' }}>
					<p style={{ color: '#666' }}>
						{searchQuery && searchQuery.trim() 
							? `No organizations found matching "${searchQuery}"`
							: intl.formatMessage(messages.noOrganizationsFound)
						}
					</p>
				</div>
			);
		}
		
		// Sort data based on current sort state
		const sortedData = this.sortOrganizations(filteredData, sortBy, sortOrder, organizationsOverdueFilter, organizationsPeriodFilter);
		
		// Pagination
		const pageSize = this.state.organizationsPageSize || 50;
		const totalItems = sortedData.length;
		const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
		let currentPage = this.state.organizationsCurrentPage || 1;
		if (currentPage > totalPages) currentPage = totalPages;
		if (currentPage < 1) currentPage = 1;
		
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = Math.min(startIndex + pageSize, totalItems);
		const visibleData = sortedData.slice(startIndex, endIndex);
		
		return (
			<div>
				<div className="table-responsive">
					<Table className="table table-bordered table-striped table-hover">
						<thead>
							<tr>
								<th style={{ textAlign: 'left' }}>
									<FormattedMessage {...messages.columnOrganization} />
								</th>
								{this.renderSortableHeader('invoiceCount', messages.columnInvoiceCount, 'center')}
								{this.renderSortableHeader('czk', messages.columnTotalCZK, 'right')}
								{this.renderSortableHeader('eur', messages.columnTotalEUR, 'right')}
								{this.renderSortableHeader('other', messages.columnTotalOther, 'right')}
								{this.renderSortableHeader('dueDate', messages.columnOldestDueDate, 'center')}
							</tr>
						</thead>
					<tbody>
						{visibleData.map(org => {
							const rows = [this.renderOrganizationRow(org)];
							if (this.state.expandedOrgGroups[org.organisationId]) {
								const expandedRows = this.renderExpandedOrganizationRows(org);
								if (expandedRows) {
									rows.push(...expandedRows);
								}
							}
							return rows;
						})}
					</tbody>
					</Table>
				</div>
			</div>
		);
	}
	
	// ========== End Organizations View Methods ==========
	
	handleViewModeChange = (newMode) => {
		this.setState({ viewMode: newMode });
		
		// Load organizations data when switching to organizations view
		if (newMode === 'organizations' && !this.state.organizationsData) {
			this.loadOrganizationsView();
		}
		
		// Update URL parameter
		if (newMode === 'organizations') {
			// Don't update URL for organizations view - it's developer-only temporary view
		} else if (newMode === 'all') {
			if (this.props.onFilterChange) {
				this.props.onFilterChange(false);
			}
		} else {
			if (this.props.onFilterChange) {
				this.props.onFilterChange(true);
			}
		}
	};

    render() {
        const { loading, error } = this.state;
        // items z Relay parenta (OverdueBillsPage):
        let items = [];
        let allItemsBeforeFilter = []; // For count calculations (before filters)
        const organisationId = this.resolveOrganisationId();
        const isDeveloper = organisationId
            ? (BrowserPermission.hasOrganisationPermission(Permission.PERM_DEVELOPER, organisationId) && BrowserPermission.developerModeEnabled(organisationId))
            : false;
        try {
            const edges = this.props.user && this.props.user.organisation && this.props.user.organisation.organisationOverdueBills && this.props.user.organisation.organisationOverdueBills.edges;
            const rawItems = Array.isArray(edges) ? edges.map(e => e && e.node).filter(Boolean) : [];
            items = rawItems.map((b, idx) => {
                const code = String(b.code || '');
                let dueDate = b.dueDate || null;
                if (typeof dueDate === 'string') {
                    if (dueDate.indexOf('T') !== -1) dueDate = dueDate.split('T')[0];
                    if (dueDate.indexOf('+') !== -1) dueDate = dueDate.split('+')[0];
                    if (dueDate.indexOf(' ') !== -1) dueDate = dueDate.split(' ')[0];
                }
                const amountToPay = String(b.amountToPay != null ? b.amountToPay : '');
                const totalAmount = String(b.totalAmount != null ? b.totalAmount : '');
                const id = String(b.id || code || idx);
                const realId = b.realId ? String(b.realId) : '';
                const isIgnored = !!b.isIgnored;
                const ignoreSource = b.ignoreSource || null;
                const isAdvancePayment = !!b.isAdvancePayment;
                let variableSymbol = String(b.variableSymbol || '');
                let currencyCode = String(b.currencyCode || '');
                if (currencyCode && currencyCode.toLowerCase().indexOf('code:') === 0) {
                    currencyCode = currencyCode.substring(5).trim();
                }
                const __expanded = { id, realId, code, variableSymbol, dueDate, amountToPay, totalAmount, currencyCode, isIgnored, ignoreSource, isAdvancePayment };
                return { id, realId, code, variableSymbol, dueDate, amountToPay, totalAmount, currencyCode, isIgnored, ignoreSource, isAdvancePayment, __raw: b, __expanded };
            });
            // Keep original items for count calculations (before any filters)
            allItemsBeforeFilter = [...items];
            // Apply search filter FIRST (searches ALL invoices, ignores pagination and filters)
            const hasActiveSearch = !!(this.props.searchQuery && this.props.searchQuery.trim());
            if (hasActiveSearch) {
                const searchLower = this.props.searchQuery.trim().toLowerCase();
                items = items.filter((item) => {
                    // Search in code (invoice number)
                    const code = String(item.code || '').toLowerCase();
                    if (code.includes(searchLower)) return true;

                    // Search in variable symbol
                    const varSym = String(item.variableSymbol || '').toLowerCase();
                    if (varSym.includes(searchLower)) return true;

                    return false;
                });
            }
            // filter: only unpaid (amountToPay > 0) - regardless of due date
            // BUT: Skip this filter when search is active (user explicitly searches for invoice)
            if (this.props.onlyUnpaidOverdue && !hasActiveSearch) {
                items = items.filter((i) => {
                    const toPay = Number(i.amountToPay || 0);
                    return toPay > 0; // show all unpaid invoices
                });
            }
            // řadit od nejmladší po nejstarší (newest first) podle dueDate
            items.sort((a, b) => {
                const ad = a.dueDate ? new Date(a.dueDate).getTime() : 0;
                const bd = b.dueDate ? new Date(b.dueDate).getTime() : 0;
                return bd - ad;
            });
        } catch (e) {}

        // Handle global search results
        if (this.props.searchGlobal && this.state.globalSearchResults && this.state.globalSearchResults.invoices) {
            // Transform global results to match local format
            items = this.state.globalSearchResults.invoices.map((result) => {
                const b = result.bill;
                const org = result.organisation;
                const id = b.realId || b.id;
                const realId = b.realId || b.id;
                const code = String(b.code || '');
                const variableSymbol = String(b.variableSymbol || '');
                const dueDate = b.dueDate;
                const amountToPay = Number(b.amountToPay || 0);
                const totalAmount = Number(b.totalAmount || 0);
                let currencyCode = String(b.currencyCode || '');
                if (currencyCode && currencyCode.toLowerCase().indexOf('code:') === 0) {
                    currencyCode = currencyCode.substring(5).trim();
                }
                return {
                    id,
                    realId,
                    code,
                    variableSymbol,
                    dueDate,
                    amountToPay,
                    totalAmount,
                    currencyCode,
                    __raw: b,
                    __organisation: org // Add organisation info for display
                };
            });
        }

        // Pagination (skip if search is active to show ALL results)
        const totalItems = items.length;
        const hasActiveSearch = !!(this.props.searchQuery && this.props.searchQuery.trim());
        const hasGlobalSearch = !!(this.props.searchGlobal && this.state.globalSearchResults);
        let visibleItems;
        let currentPage = 1;
        let totalPages = 1;

        if (hasActiveSearch || hasGlobalSearch) {
            // Show ALL search results without pagination
            visibleItems = items;
        } else {
            // Normal pagination
        const pageSize = this.state.pageSize || 50;
            totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
            currentPage = this.state.currentPage || 1;
        if (currentPage > totalPages) currentPage = totalPages;
        if (currentPage < 1) currentPage = 1;
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = Math.min(startIndex + pageSize, totalItems);
            visibleItems = items.slice(startIndex, endIndex);
        }
        
        // Override pagination for organizations view
        if (this.state.viewMode === 'organizations' && this.state.organizationsData) {
            const orgPageSize = this.state.organizationsPageSize || 50;
            // CRITICAL: Use filtered data count, not raw data!
            const filteredOrgs = this.getFilteredOrganizationsData(
                this.state.organizationsData,
                this.state.organizationsBlockedFilter,
                this.state.organizationsOverdueFilter,
                this.props.searchQuery
            );
            const orgTotalItems = filteredOrgs.length;
            totalPages = Math.max(1, Math.ceil(orgTotalItems / orgPageSize));
            currentPage = this.state.organizationsCurrentPage || 1;
        }
        const selectedCount = Object.keys(this.state.selectedIds).filter(k => this.state.selectedIds[k]).length;
        return (
            <AppPanel title={this.props.intl ? this.props.intl.formatMessage(this.props.onlyUnpaidOverdue ? messages.titleUnpaidOverdue : messages.title) : 'Overdue Invoices'}>
                <div className="panel-body">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="pull-right" style={{ marginBottom: '20px' }}>
                                {isDeveloper ? (
                                <div className="dropdown" style={{ display: 'inline-block' }}>
                                    <button
                                        className="btn btn-default dropdown-toggle"
                                        data-toggle="dropdown"
                                        disabled={this.state.loading}
                                        id="developer-actions-dropdown"
                                        type="button"
                                    >
                                        {this.state.loading ? (this.props.intl ? this.props.intl.formatMessage({ id: 'common.loading', defaultMessage: 'Loading…' }) : 'Loading…') : (this.props.intl ? this.props.intl.formatMessage(messages.developerActions) : 'Actions')}
                                        {' '}
                                        <span className="caret"></span>
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-left" style={{ left: 'auto', right: 0 }}>
                                        {/*
                                            Hidden by request (12/2025)
                                        */}
                                        {false && (
                                            <li>
                                                <a href="#" onClick={(e) => { e.preventDefault(); this.handleLoadInvoicesOrg(); }}>
                                                    <i className="fa fa-download" style={{ marginRight: 6 }}></i>
                                                    <FormattedMessage {...messages.loadFromFlexiOrg} />
                                                </a>
                                            </li>
                                        )}
                                        <li>
                                            <a href="#" onClick={(e) => { e.preventDefault(); this.handleLoadInvoicesAll(); }}>
                                                <i className="fa fa-download" style={{ marginRight: 6 }}></i>
                                    <FormattedMessage {...messages.loadFromFlexiAll} />
                                            </a>
                                        </li>
                                        <li className="divider"></li>
                                        <li>
                                            <a href="#" onClick={(e) => { e.preventDefault(); this.handleRunRulesOrg(); }}>
                                                <i className="fa fa-play-circle" style={{ marginRight: 6 }}></i>
                                    <FormattedMessage defaultMessage="Run rules (this org.)" id="invoices.notifyOrg" />
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" onClick={(e) => { e.preventDefault(); this.handleRunRulesAll(); }}>
                                                <i className="fa fa-play-circle" style={{ marginRight: 6 }}></i>
                                    <FormattedMessage defaultMessage="Run rules (all org.)" id="invoices.notifyAll" />
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    {loading && <div>Loading...</div>}
                    {error && <div style={{color: 'red'}}>{String(error)}</div>}
                    <div className="row" style={{ marginBottom: '10px' }}>
                        <div className="col-md-12">
                            {/* Filter toggle with button group (with Organizations view for developers) */}
                            {(() => {
                                // Calculate counts from original items (before filters)
                                const totalCount = allItemsBeforeFilter.length;
                                const unpaidCount = allItemsBeforeFilter.filter(i => Number(i.amountToPay || 0) > 0).length;
                                const { viewMode } = this.state;

                                return (
                                    <div className="btn-group" role="group" style={{ marginRight: 20, display: 'inline-block', verticalAlign: 'top' }}>
                                        <button
                                            className={`btn btn-sm ${viewMode === 'all' ? 'btn-primary' : 'btn-default'}`}
                                            onClick={() => this.handleViewModeChange('all')}
                                            style={{ width: 165 }}
                                            type="button"
                                        >
                                            <i className="fa fa-list" style={{ marginRight: 6 }}></i>
                                            <FormattedMessage {...messages.filterAll} />
                                            {' '}
                                            <span className={`badge ${viewMode === 'all' ? 'badge-light' : ''}`} style={{ backgroundColor: viewMode === 'all' ? '#fff' : undefined, color: viewMode === 'all' ? '#337ab7' : undefined }}>{totalCount}</span>
                                        </button>
                                        <button
                                            className={`btn btn-sm ${viewMode === 'unpaid' ? 'btn-primary' : 'btn-default'}`}
                                            onClick={() => this.handleViewModeChange('unpaid')}
                                            style={{ width: 165 }}
                                            type="button"
                                        >
                                            <i className="fa fa-exclamation-circle" style={{ marginRight: 6 }}></i>
                                            <FormattedMessage {...messages.filterUnpaid} />
                                            {' '}
                                            <span className={`badge ${viewMode === 'unpaid' ? 'badge-light' : ''}`} style={{ backgroundColor: viewMode === 'unpaid' ? '#fff' : undefined, color: viewMode === 'unpaid' ? '#337ab7' : undefined }}>{unpaidCount}</span>
                                        </button>
										{isDeveloper && (
											<button
												className={`btn btn-sm ${viewMode === 'organizations' ? 'btn-primary' : 'btn-default'}`}
												onClick={() => this.handleViewModeChange('organizations')}
												style={{ width: 165 }}
												type="button"
											>
												<i className="fa fa-building-o" style={{ marginRight: 6 }}></i>
												<FormattedMessage {...messages.filterOrganizations} />
											</button>
										)}
                                    </div>
                                );
                            })()}
                            <div style={{ display: 'inline-block', marginRight: 20, verticalAlign: 'top' }}>
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                <input
                                        className="form-control input-sm"
                                        onChange={(e) => {
                                            if (this.props.onSearchChange) {
                                                this.props.onSearchChange(e.target.value);
                                            }
                                        }}
                                        placeholder={this.props.intl ? this.props.intl.formatMessage(
                                            this.state.viewMode === 'organizations' 
                                                ? messages.searchOrganizationsPlaceholder 
                                                : messages.searchPlaceholder
                                        ) : 'Search...'}
                                        style={{ paddingRight: 30, minWidth: 250 }}
                                        type="text"
                                        value={this.props.searchQuery || ''}
                                    />
                                    {this.props.searchQuery ? (
                                        <button
                                            onClick={() => {
                                                if (this.props.onSearchChange) {
                                                    this.props.onSearchChange('');
                                                }
                                            }}
                                            style={{
                                                position: 'absolute',
                                                right: 5,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                border: 'none',
                                                background: 'transparent',
                                                cursor: 'pointer',
                                                fontSize: 18,
                                                color: '#999'
                                            }}
                                            type="button"
                                        >
                                            ×
                                        </button>
                                    ) : (
                                        <i className="fa fa-search" style={{
                                            position: 'absolute',
                                            right: 10,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#999'
                                        }}></i>
                                    )}
                                </div>
                            </div>
                            {isDeveloper && this.state.viewMode !== 'organizations' ? (
                                <label style={{ marginLeft: 10, fontWeight: 'normal', cursor: 'pointer' }}>
                                    <input
                                        checked={!!this.props.searchGlobal}
                                        onChange={(e) => {
                                            if (this.props.onSearchGlobalChange) {
                                                this.props.onSearchGlobalChange(!!e.target.checked);
                                            }
                                        }}
                                    style={{ marginRight: 6 }}
                                    type="checkbox"
                                />
                                    <FormattedMessage {...messages.searchGlobal} />
                            </label>
                            ) : null}
                            <div className="pull-right">
                                <label className="control-label" style={{ marginRight: 6 }}>
                                    <FormattedMessage {...messages.pageSizeLabel} />:
                                </label>
                                <select
                                    className="form-control input-sm"
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value, 10);
                                        if (this.state.viewMode === 'organizations') {
                                            this.setState({ organizationsPageSize: isNaN(val) ? 50 : val, organizationsCurrentPage: 1 });
                                        } else {
                                            this.setState({ pageSize: isNaN(val) ? 50 : val, currentPage: 1 });
                                        }
                                    }}
                                    style={{ display: 'inline-block', width: 80 }}
                                    value={this.state.viewMode === 'organizations' ? String(this.state.organizationsPageSize || 50) : String(this.state.pageSize)}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                    <option value="200">200</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    {this.state.debug && (
                        <div style={{margin:'8px 0', padding:8, background:'#f8f8f8', border:'1px solid #eee'}}>
                            <div><strong>Debug URL:</strong> <code>{String(this.state.debug.urlPart || '')}</code></div>
                            <details style={{marginTop:6}}>
                                <summary>Raw response</summary>
                                {this.state.debug.raw ? (
                                    <pre style={{whiteSpace:'pre-wrap', wordBreak:'break-all'}}>{JSON.stringify(this.state.debug.raw, null, 2)}</pre>
                                ) : null}
                                {this.state.debug.rawText != null ? (
                                    <pre style={{whiteSpace:'pre-wrap', wordBreak:'break-all'}}>{
                                        typeof this.state.debug.rawText === 'string'
                                            ? this.state.debug.rawText
                                            : JSON.stringify(this.state.debug.rawText, null, 2)
                                    }</pre>
                                ) : null}
                            </details>
                        </div>
                    )}
                    {this.state.viewMode !== 'organizations' && this.props.searchQuery && this.props.searchQuery.trim() ? (
                        <div style={{ marginBottom: 10, fontSize: 14 }}>
                            {visibleItems.length > 0 ? (
                                <span style={{ color: '#31708f' }}>
                                    <i className="fa fa-info-circle" style={{ marginRight: 5 }}></i>
                                    <FormattedMessage
                                        {...messages.searchResultsCount}
                                        values={{ count: visibleItems.length, query: this.props.searchQuery }}
                                    />
                                    {hasGlobalSearch && this.state.globalSearchResults && this.state.globalSearchResults.invoices ? (
                                        <span>
                                            {' - '}
                                            <FormattedMessage
                                                {...messages.searchGlobalResults}
                                                values={{
                                                    orgs: Array.from(new Set(this.state.globalSearchResults.invoices.map(r => r.organisation.name))).join(', ')
                                                }}
                                            />
                                        </span>
                                    ) : null}
                                </span>
                            ) : (
                                <span style={{ color: '#a94442' }}>
                                    <i className="fa fa-exclamation-circle" style={{ marginRight: 5 }}></i>
                                    <FormattedMessage
                                        {...messages.searchNoResults}
                                        values={{ query: this.props.searchQuery }}
                                    />
                                </span>
                            )}
                        </div>
                    ) : null}
                    {this.state.globalSearchError ? (
                        <div style={{ marginBottom: 10, fontSize: 14 }}>
                            <span style={{ color: '#a94442' }}>
                                <i className="fa fa-exclamation-triangle" style={{ marginRight: 5 }}></i>
                                {this.state.globalSearchError}
                            </span>
                        </div>
                    ) : null}
                    {/* Total unpaid amounts by currency - show for all views */}
                    {(() => {
                        const totalsByCurrency = {};
                        
                        // Calculate totals based on current view mode
                        if (this.state.viewMode === 'organizations') {
                            // In organizations view: use deduplicated global summary
                            // Select amounts based on active filters (combining both blocked and overdue)
                            const summary = this.state.globalUnpaidSummary;
                            const overdueFilter = this.state.organizationsOverdueFilter;
                            const blockedFilter = this.state.organizationsBlockedFilter;
                            const periodFilter = this.state.organizationsPeriodFilter;
                            
                            if (summary) {
                                let czk, eur, other;
                                
                                // Select amounts based on BOTH filters combined
                                if (periodFilter && periodFilter !== 'all') {
                                    // Overdue by specific bucket (optionally combined with blocked filter)
                                    // If Status = "Unpaid not overdue", the intersection is empty.
                                    if (overdueFilter === 'unpaid') {
                                        // Bucket up30 contains not-overdue (0 days); other buckets are empty.
                                        if (periodFilter === 'up30') {
                                            if (blockedFilter === 'blocked') {
                                                czk = Number(summary.totalBlockedNotOverdueCZK || 0);
                                                eur = Number(summary.totalBlockedNotOverdueEUR || 0);
                                                other = Number(summary.totalBlockedNotOverdueOther || 0);
                                            } else if (blockedFilter === 'notBlocked') {
                                                czk = Number(summary.totalNotBlockedNotOverdueCZK || 0);
                                                eur = Number(summary.totalNotBlockedNotOverdueEUR || 0);
                                                other = Number(summary.totalNotBlockedNotOverdueOther || 0);
                                            } else {
                                                czk = Number(summary.totalNotOverdueCZK || 0);
                                                eur = Number(summary.totalNotOverdueEUR || 0);
                                                other = Number(summary.totalNotOverdueOther || 0);
                                            }
                                        } else {
                                            czk = 0; eur = 0; other = 0;
                                        }
                                    } else {
                                        const suffix =
                                            periodFilter === 'up30' ? 'Up30'
                                            : periodFilter === '31to60' ? '31to60'
                                            : periodFilter === '61to90' ? '61to90'
                                            : 'Over90';
                                        
                                        const prefix =
                                            blockedFilter === 'blocked' ? 'totalBlockedOverdue'
                                            : blockedFilter === 'notBlocked' ? 'totalNotBlockedOverdue'
                                            : 'totalOverdue';
                                        
                                        czk = Number(summary[`${prefix}${suffix}CZK`] || 0);
                                        eur = Number(summary[`${prefix}${suffix}EUR`] || 0);
                                        other = Number(summary[`${prefix}${suffix}Other`] || 0);
                                        // If Status = Overdue and bucket is up30, subtract not-overdue (0 days) part.
                                        if (overdueFilter === 'overdue' && periodFilter === 'up30') {
                                            if (blockedFilter === 'blocked') {
                                                czk -= Number(summary.totalBlockedNotOverdueCZK || 0);
                                                eur -= Number(summary.totalBlockedNotOverdueEUR || 0);
                                                other -= Number(summary.totalBlockedNotOverdueOther || 0);
                                            } else if (blockedFilter === 'notBlocked') {
                                                czk -= Number(summary.totalNotBlockedNotOverdueCZK || 0);
                                                eur -= Number(summary.totalNotBlockedNotOverdueEUR || 0);
                                                other -= Number(summary.totalNotBlockedNotOverdueOther || 0);
                                            } else {
                                                czk -= Number(summary.totalNotOverdueCZK || 0);
                                                eur -= Number(summary.totalNotOverdueEUR || 0);
                                                other -= Number(summary.totalNotOverdueOther || 0);
                                            }
                                        }
                                    }
                                } else if (blockedFilter === 'blocked' && overdueFilter === 'overdue') {
                                    // Blocked + Overdue (all periods)
                                    czk = Number(summary.totalBlockedOverdueCZK || 0);
                                    eur = Number(summary.totalBlockedOverdueEUR || 0);
                                    other = Number(summary.totalBlockedOverdueOther || 0);
                                } else if (blockedFilter === 'blocked' && overdueFilter === 'unpaid') {
                                    // Blocked + Not Overdue
                                    czk = Number(summary.totalBlockedNotOverdueCZK || 0);
                                    eur = Number(summary.totalBlockedNotOverdueEUR || 0);
                                    other = Number(summary.totalBlockedNotOverdueOther || 0);
                                } else if (blockedFilter === 'notBlocked' && overdueFilter === 'overdue') {
                                    // Not Blocked + Overdue
                                    czk = Number(summary.totalNotBlockedOverdueCZK || 0);
                                    eur = Number(summary.totalNotBlockedOverdueEUR || 0);
                                    other = Number(summary.totalNotBlockedOverdueOther || 0);
                                } else if (blockedFilter === 'notBlocked' && overdueFilter === 'unpaid') {
                                    // Not Blocked + Not Overdue
                                    czk = Number(summary.totalNotBlockedNotOverdueCZK || 0);
                                    eur = Number(summary.totalNotBlockedNotOverdueEUR || 0);
                                    other = Number(summary.totalNotBlockedNotOverdueOther || 0);
                                } else if (blockedFilter === 'blocked') {
                                    // Show only blocked organizations amounts (all: overdue + not overdue)
                                    czk = Number(summary.totalBlockedCZK || 0);
                                    eur = Number(summary.totalBlockedEUR || 0);
                                    other = Number(summary.totalBlockedOther || 0);
                                } else if (blockedFilter === 'notBlocked') {
                                    // Show only not blocked organizations amounts (all: overdue + not overdue)
                                    czk = Number(summary.totalNotBlockedCZK || 0);
                                    eur = Number(summary.totalNotBlockedEUR || 0);
                                    other = Number(summary.totalNotBlockedOther || 0);
                                } else if (overdueFilter === 'overdue') {
                                    // Show only overdue amounts (all: blocked + not blocked)
                                    czk = Number(summary.totalOverdueCZK || 0);
                                    eur = Number(summary.totalOverdueEUR || 0);
                                    other = Number(summary.totalOverdueOther || 0);
                                } else if (overdueFilter === 'unpaid') {
                                    // Show only not overdue amounts (all: blocked + not blocked)
                                    czk = Number(summary.totalNotOverdueCZK || 0);
                                    eur = Number(summary.totalNotOverdueEUR || 0);
                                    other = Number(summary.totalNotOverdueOther || 0);
                                } else {
                                    // Show all unpaid (all: blocked + not blocked, overdue + not overdue)
                                    czk = Number(summary.totalCZK || 0);
                                    eur = Number(summary.totalEUR || 0);
                                    other = Number(summary.totalOther || 0);
                                }
                                
                                // Add to totals
                                if (czk > 0) {
                                    totalsByCurrency['CZK'] = czk;
                                }
                                if (eur > 0) {
                                    totalsByCurrency['EUR'] = eur;
                                }
                                
                                const otherCurrency = summary.otherCurrency;
                                if (other > 0 && otherCurrency) {
                                    totalsByCurrency[otherCurrency] = other;
                                }
                            }
                        } else {
                            // In invoice list views: calculate from ALL invoices (not just current page)
                            items.forEach((item) => {
                                const amountToPay = Number(item.amountToPay || 0);
                                if (amountToPay > 0) { // Only unpaid
                                    const currency = item.currencyCode || 'CZK';
                                    if (!totalsByCurrency[currency]) {
                                        totalsByCurrency[currency] = 0;
                                    }
                                    totalsByCurrency[currency] += amountToPay;
                                }
                            });
                        }

                        const currencies = Object.keys(totalsByCurrency).sort();
                        if (currencies.length === 0) return null;

                        const formatCzechNumber = (value) => {
                            if (value == null || value === '') return '';
                            const num = Number(value);
                            if (Number.isNaN(num)) return String(value);
                            const formatted = num.toFixed(2);
                            const [integerPart, decimalPart] = formatted.split('.');
                            const withSpaces = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
                            return `${withSpaces},${decimalPart}`;
                        };

                        return (
                            <div style={{
                                marginBottom: 15,
                                padding: '10px 15px',
                                backgroundColor: '#d9edf7',
                                border: '1px solid #bce8f1',
                                borderRadius: 4,
                                fontSize: 14
                            }}>
                                <strong>
                                    <i className="fa fa-calculator" style={{ marginRight: 8 }}></i>
                                    <FormattedMessage {...messages.totalUnpaid} />
                                </strong>
                                {' '}
                                {currencies.map((currency, idx) => (
                                    <span key={currency}>
                                        {idx > 0 ? ' | ' : ''}
                                        <strong style={{ fontSize: 16 }}>
                                            {formatCzechNumber(totalsByCurrency[currency])}
                                        </strong>
                                        {' '}
                                        {currency}
                                    </span>
                                ))}
                            </div>
                        );
                    })()}
					
				{/* Conditional rendering based on viewMode */}
				{this.state.viewMode === 'organizations' ? (
					// Organizations view
					<div className="row">
						<div className="col-md-12">
							{/* Filters for Organizations view */}
							<div style={{ marginBottom: 15 }}>
								{/* Overdue filter */}
								<div style={{ display: 'inline-block', marginRight: 20, verticalAlign: 'top' }}>
									<label style={{ marginRight: 10, fontWeight: 'normal' }}>
										<FormattedMessage id="invoices.organizations.statusFilterLabel" defaultMessage="Status:" />
									</label>
									<div className="btn-group" role="group">
										<button
											className={`btn btn-sm ${this.state.organizationsOverdueFilter === 'all' ? 'btn-primary' : 'btn-default'}`}
											onClick={() => this.setState({ organizationsOverdueFilter: 'all', organizationsCurrentPage: 1 })}
											type="button"
										>
											<FormattedMessage {...messages.orgFilterAll} />
										</button>
										<button
											className={`btn btn-sm ${this.state.organizationsOverdueFilter === 'unpaid' ? 'btn-primary' : 'btn-default'}`}
											onClick={() => this.setState({ organizationsOverdueFilter: 'unpaid', organizationsCurrentPage: 1 })}
											type="button"
										>
											<FormattedMessage {...messages.orgFilterUnpaid} />
										</button>
										<button
											className={`btn btn-sm ${this.state.organizationsOverdueFilter === 'overdue' ? 'btn-primary' : 'btn-default'}`}
											onClick={() => this.setState({ organizationsOverdueFilter: 'overdue', organizationsCurrentPage: 1 })}
											type="button"
										>
											<i className="fa fa-clock-o" style={{ marginRight: 5 }}></i>
											<FormattedMessage {...messages.orgFilterOverdue} />
										</button>
									</div>
								</div>
								
								{/* Period filter (overdue only) */}
								<div style={{ display: 'inline-block', marginRight: 20, verticalAlign: 'top' }}>
									<label style={{ marginRight: 10, fontWeight: 'normal' }}>
										<FormattedMessage {...messages.orgPeriodLabel} />
									</label>
									<select
										className="form-control input-sm"
										style={{ display: 'inline-block', width: 180 }}
										value={this.state.organizationsPeriodFilter}
										onChange={(e) => {
											const val = e.target.value;
											this.setState({
												organizationsPeriodFilter: val,
												organizationsCurrentPage: 1
											});
										}}
									>
										<option value="all">{this.props.intl.formatMessage(messages.orgPeriodAll)}</option>
										<option value="up30">{this.props.intl.formatMessage(messages.orgPeriodUp30)}</option>
										<option value="31to60">{this.props.intl.formatMessage(messages.orgPeriod31to60)}</option>
										<option value="61to90">{this.props.intl.formatMessage(messages.orgPeriod61to90)}</option>
										<option value="over90">{this.props.intl.formatMessage(messages.orgPeriodOver90)}</option>
									</select>
								</div>

								{/* Blocked filter */}
								<div style={{ display: 'inline-block', verticalAlign: 'top' }}>
									<label style={{ marginRight: 10, fontWeight: 'normal' }}>
										<FormattedMessage id="invoices.organizations.blockedFilterLabel" defaultMessage="Blocked:" />
									</label>
									<div className="btn-group" role="group">
										<button
											className={`btn btn-sm ${this.state.organizationsBlockedFilter === 'all' ? 'btn-primary' : 'btn-default'}`}
											onClick={() => this.setState({ organizationsBlockedFilter: 'all', organizationsCurrentPage: 1 })}
											type="button"
										>
											<FormattedMessage {...messages.filterBlockedAll} />
										</button>
										<button
											className={`btn btn-sm ${this.state.organizationsBlockedFilter === 'blocked' ? 'btn-primary' : 'btn-default'}`}
											onClick={() => this.setState({ organizationsBlockedFilter: 'blocked', organizationsCurrentPage: 1 })}
											type="button"
										>
											<i className="fa fa-ban" style={{ marginRight: 5 }}></i>
											<FormattedMessage {...messages.filterBlockedOnly} />
										</button>
										<button
											className={`btn btn-sm ${this.state.organizationsBlockedFilter === 'notBlocked' ? 'btn-primary' : 'btn-default'}`}
											onClick={() => this.setState({ organizationsBlockedFilter: 'notBlocked', organizationsCurrentPage: 1 })}
											type="button"
										>
											<FormattedMessage {...messages.filterNotBlocked} />
										</button>
									</div>
								</div>
							</div>
							{this.renderOrganizationsTable()}
							<div className="pull-right" style={{ marginTop: 10 }}>
								<div className="btn-group">
									<button 
										className="btn btn-default btn-sm" 
										disabled={currentPage <= 1} 
										onClick={() => {
											this.setState({ organizationsCurrentPage: (this.state.organizationsCurrentPage || 1) - 1 });
										}}
									>
										{'«'}
									</button>
									<span className="btn btn-default btn-sm disabled">{currentPage} / {totalPages}</span>
									<button 
										className="btn btn-default btn-sm" 
										disabled={currentPage >= totalPages} 
										onClick={() => {
											this.setState({ organizationsCurrentPage: (this.state.organizationsCurrentPage || 1) + 1 });
										}}
									>
										{'»'}
									</button>
								</div>
							</div>
							<div style={{ clear: 'both' }}></div>
						</div>
					</div>
				) : (
					// Invoice list view (default)
                    <div className="row">
                        <div className="col-md-12">
                            <Table bordered hover responsive striped>
                                <thead>
                                    <tr>
										{isDeveloper ? (
											<th style={{width: 28, textAlign:'center', verticalAlign: 'middle'}}>
											<input
												checked={visibleItems.length > 0 && visibleItems.every(it => this.state.selectedIds[it.id])}
												onChange={(e) => this.handleToggleSelectAll(visibleItems, !!e.target.checked)}
												type="checkbox"
											/>
										</th>
										) : null}
                                        {hasGlobalSearch ? (
                                            <th style={{textAlign:'left', verticalAlign: 'middle'}}><FormattedMessage {...messages.columnOrganisation} /></th>
                                        ) : null}
                                        <th style={{textAlign:'center', verticalAlign: 'middle'}}><FormattedMessage {...messages.columnCode} /></th>
                                        <th style={{textAlign:'center', verticalAlign: 'middle'}}><FormattedMessage {...messages.columnVariableSymbol} /></th>
                                        <th style={{textAlign:'center', verticalAlign: 'middle'}}><FormattedMessage {...messages.columnDueDate} /></th>
                                        <th style={{textAlign:'right', verticalAlign: 'middle'}}><FormattedMessage {...messages.columnTotalAmount} /></th>
                                        <th style={{textAlign:'right', verticalAlign: 'middle'}}><FormattedMessage {...messages.columnAmountToPay} /></th>
                                        <th style={{textAlign:'center', verticalAlign: 'middle', display: 'none'}}><FormattedMessage {...messages.columnCurrency} /></th>
                                        <th style={{textAlign:'center', verticalAlign: 'middle'}}><FormattedMessage {...messages.columnDaysOverdue} /></th>
                                        <th style={{textAlign:'center', verticalAlign: 'middle'}}><FormattedMessage {...messages.columnDocuments} /></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleItems.map((i) => this.renderRow(i))}
                                </tbody>
                            </Table>
                            {isDeveloper ? (
                                <div className="pull-left" style={{ marginTop: 10 }}>
                                    <button
                                        className="btn btn-danger btn-sm"
                                        disabled={selectedCount === 0}
                                        onClick={() => this.handleBulkDelete(items)}
                                    >
                                        <FormattedMessage {...messages.deleteSelected} />
                                    </button>
                                    {' '}
                                    <button
                                        className="btn btn-default btn-sm"
                                        disabled={selectedCount === 0}
                                        onClick={() => this.handleBulkIgnore(items)}
                                    >
                                        <FormattedMessage {...messages.ignoreSelected} />
                                    </button>
                                    {' '}
                                    <button
                                        className="btn btn-default btn-sm"
                                        disabled={selectedCount === 0}
                                        onClick={() => this.handleBulkUnignore(items)}
                                    >
                                        <FormattedMessage {...messages.unignoreSelected} />
                                    </button>
                                </div>
                            ) : null}
							<div className="pull-right" style={{ marginTop: 10 }}>
								<div className="btn-group">
									<button 
										className="btn btn-default btn-sm" 
										disabled={currentPage <= 1} 
										onClick={() => {
											if (this.state.viewMode === 'organizations') {
												this.setState({ organizationsCurrentPage: (this.state.organizationsCurrentPage || 1) - 1 });
											} else {
												this.setState({ currentPage: currentPage - 1 });
											}
										}}
									>
										{'«'}
									</button>
									<span className="btn btn-default btn-sm disabled">{currentPage} / {totalPages}</span>
									<button 
										className="btn btn-default btn-sm" 
										disabled={currentPage >= totalPages} 
										onClick={() => {
											if (this.state.viewMode === 'organizations') {
												this.setState({ organizationsCurrentPage: (this.state.organizationsCurrentPage || 1) + 1 });
											} else {
												this.setState({ currentPage: currentPage + 1 });
											}
										}}
									>
										{'»'}
									</button>
								</div>
							</div>
							<div style={{ clear: 'both', marginTop: 60, padding: '15px', backgroundColor: '#f9f9f9', border: '1px solid #e3e3e3', borderRadius: 4 }}>
								<strong style={{ marginRight: 15 }}><FormattedMessage {...messages.legendTitle} />:</strong>
								<span style={{ marginRight: 20 }}>
									<span style={{ display: 'inline-block', width: 16, height: 16, backgroundColor: '#dff0d8', border: '1px solid #d6e9c6', marginRight: 5, verticalAlign: 'middle' }}></span>
									<FormattedMessage {...messages.legendPaid} />
								</span>
								<span style={{ marginRight: 20 }}>
									<span style={{ display: 'inline-block', width: 16, height: 16, backgroundColor: '#fcf8e3', border: '1px solid #faebcc', marginRight: 5, verticalAlign: 'middle' }}></span>
									<FormattedMessage {...messages.legendPartial} />
								</span>
								<span style={{ marginRight: 20 }}>
									<span style={{ display: 'inline-block', width: 16, height: 16, backgroundColor: '#f2dede', border: '1px solid #ebccd1', marginRight: 5, verticalAlign: 'middle' }}></span>
									<FormattedMessage {...messages.legendOverdue} />
								</span>
								{(isDeveloper || BrowserPermission.isPartner1(organisationId) || BrowserPermission.isPartner2(organisationId)) ? (
									<span>
										<span style={{ display: 'inline-block', width: 16, height: 16, backgroundColor: '#e8f4f8', border: '3px solid #0088cc', marginRight: 5, verticalAlign: 'middle' }}></span>
										<FormattedMessage {...messages.legendSelected} />
									</span>
								) : null}
							</div>
                        </div>
                    </div>
					)}
					{/* End conditional rendering */}
                </div>
            </AppPanel>
        );
    }
}

OverdueBillsList.propTypes = {
    intl: PropTypes.object,
    onFilterChange: PropTypes.func,
    onRefresh: PropTypes.func,
    onSearchChange: PropTypes.func,
    onSearchGlobalChange: PropTypes.func,
    onSelect: PropTypes.func,
    onlyUnpaidOverdue: PropTypes.bool,
    organisationId: PropTypes.string,
    searchGlobal: PropTypes.bool,
    searchQuery: PropTypes.string,
    selected: PropTypes.object,
    user: PropTypes.object
};

OverdueBillsList.defaultProps = {
    onlyUnpaidOverdue: true
};

export default injectIntl(OverdueBillsList);
